import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { getNominationRoundYear } from '../../../utils/getNominationRoundYear.js';
import { stringUcFirst } from '../../../utils/stringUcFirst.js';

const ExportButton = ({
  candidates,
  committeeMembers,
  getCOIForCandidateAndMember,
  committeeName,
}) => {
  const exportToCSV = () => {
    if (!candidates.length || !committeeMembers.length) return;

    const headers = [
      'Candidate Name',
      'Data Type',
      ...committeeMembers.map((member) => member.name),
    ];
    const rows = [headers];

    candidates.forEach((candidate) => {
      const typeRow = [candidate.name, 'Type'];
      committeeMembers.forEach((member) => {
        const coi = getCOIForCandidateAndMember(candidate.id, member.id);
        const cleanTypes = coi.types
          .replace(/<br\s*\/?>/gi, '; ')
          .replace(/<[^>]*>/g, '');

        let formattedType = cleanTypes || '';

        if (coi.category && cleanTypes) {
          formattedType = `${stringUcFirst(coi.category)}: ${cleanTypes}`;
        }

        typeRow.push(formattedType);
      });
      rows.push(typeRow);

      const detailRow = ['', 'Detail'];
      committeeMembers.forEach((member) => {
        const coi = getCOIForCandidateAndMember(candidate.id, member.id);
        const detail = coi.detail ? coi.detail.replace(/"/g, '""') : '';
        detailRow.push(detail);
      });
      rows.push(detailRow);
    });

    const csvContent = rows
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell || '');

            const safeCell = /^[=\-+@]/.test(cellStr) ? `'${cellStr}` : cellStr;
            const escapedCell = safeCell.replace(/"/g, '""');
            return `"${escapedCell}"`;
          })
          .join(','),
      )
      .join('\n');

    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    const blob = new Blob([csvWithBOM], {
      type: 'text/csv;charset=utf-8',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `COI_Report_${committeeName.replace(/\s+/g, '_')}_${getNominationRoundYear(new Date())}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Button
      variant="primary"
      className="standard-width-button d-flex align-items-center gap-2"
      onClick={exportToCSV}
    >
      <i className="bi bi-download" />
      Export
    </Button>
  );
};

ExportButton.propTypes = {
  candidates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  committeeMembers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      isChair: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  getCOIForCandidateAndMember: PropTypes.func.isRequired,
  committeeName: PropTypes.string.isRequired,
};

export const ExportAllButton = ({
  committees,
  getCOIForCandidateAndMember,
  showCouncilMembersOnly,
}) => {
  const exportAllToCSV = () => {
    if (!committees.length) return;

    const rows = [];

    committees.forEach((committee, index) => {
      const { candidates, committeeMembers, name } = committee;
      if (!candidates.length || !committeeMembers.length) return;

      if (index > 0) {
        rows.push([]);
        rows.push([]);
        rows.push([]);
      }

      rows.push([name]);

      const headers = [
        'Candidate Name',
        'Data Type',
        ...committeeMembers.map((member) => member.name),
      ];
      rows.push(headers);

      candidates.forEach((candidate) => {
        const typeRow = [candidate.name, 'Type'];
        committeeMembers.forEach((member) => {
          const coi = getCOIForCandidateAndMember(candidate.id, member.id);
          const cleanTypes = coi.types
            .replace(/<br\s*\/?>/gi, '; ')
            .replace(/<[^>]*>/g, '');

          let formattedType = cleanTypes || '';

          if (coi.category && cleanTypes) {
            formattedType = `${stringUcFirst(coi.category)}: ${cleanTypes}`;
          }

          typeRow.push(formattedType);
        });
        rows.push(typeRow);

        const detailRow = ['', 'Detail'];
        committeeMembers.forEach((member) => {
          const coi = getCOIForCandidateAndMember(candidate.id, member.id);
          const detail = coi.detail ? coi.detail.replace(/"/g, '""') : '';
          detailRow.push(detail);
        });
        rows.push(detailRow);
      });
    });

    const csvContent = rows
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell || '');
            const safeCell = /^[=\-+@]/.test(cellStr) ? `'${cellStr}` : cellStr;
            const escapedCell = safeCell.replace(/"/g, '""');
            return `"${escapedCell}"`;
          })
          .join(','),
      )
      .join('\n');

    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    const blob = new Blob([csvWithBOM], {
      type: 'text/csv;charset=utf-8',
    });

    const suffix = showCouncilMembersOnly
      ? 'Council_Members'
      : 'All_Committees';

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `COI_Report_${suffix}_${getNominationRoundYear(new Date())}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const buttonText = showCouncilMembersOnly
    ? 'Export All Council COI Declarations'
    : 'Export All Chair & SC COI Declarations';

  return (
    <div className="d-flex justify-content-center mt-xl-5 mb-4">
      <Button
        variant="primary"
        onClick={exportAllToCSV}
        disabled={!committees.length}
        className="d-flex align-items-center gap-2"
      >
        <i className="fas fa-file-export" />
        {buttonText}
      </Button>
    </div>
  );
};

ExportAllButton.propTypes = {
  committees: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      candidates: PropTypes.array.isRequired,
      committeeMembers: PropTypes.array.isRequired,
    }),
  ).isRequired,
  getCOIForCandidateAndMember: PropTypes.func.isRequired,
  showCouncilMembersOnly: PropTypes.bool.isRequired,
};

export default ExportButton;
